import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { sendPasswordResetEmail, testResendConfiguration, testFirebaseConfiguration } from '../services/authService'; // Import the test functions

function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [testResults, setTestResults] = useState({
    resend: null,
    firebase: null
  });

  // Test configurations on component mount but don't show status
  useEffect(() => {
    const runTests = async () => {
      try {
        // Test Resend configuration
        console.log('Testing Resend configuration on page load...');
        const resendResult = await testResendConfiguration();
        
        // Test Firebase configuration
        console.log('Testing Firebase Admin configuration on page load...');
        const firebaseResult = await testFirebaseConfiguration();
        
        setTestResults({
          resend: resendResult,
          firebase: firebaseResult
        });
        
        // Log results for debugging
        console.log('Configuration test results:', { resend: resendResult, firebase: firebaseResult });
        
        // Only show error messages if services aren't configured
        if (!resendResult.resendConfigured || firebaseResult.status !== 'success') {
          setError('Unable to connect to required services. Please try again later or contact support.');
        }
      } catch (err) {
        console.error('Error testing service configurations:', err);
        setError('Error connecting to services. Please try again later.');
      }
    };
    
    runTests();
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setMessage('');
    setError('');
    setLoading(true);

    try {
      // Check if this is a local account first
      try {
        const localUsers = JSON.parse(localStorage.getItem('local_users') || '[]');
        const isLocal = localUsers.some(u => u.email.toLowerCase() === email.toLowerCase());
        if (isLocal) {
          setError('This email is registered as a Local Account. Local Accounts do not support Cloud password reset. Please log in with your local password.');
          setLoading(false);
          return;
        }
      } catch (localCheckErr) {
        console.error('Error checking local users:', localCheckErr);
      }

      // First check if our required services are configured
      if (testResults.resend && !testResults.resend.resendConfigured) {
        setError('Email service is not properly configured. Please contact support.');
        setLoading(false);
        return;
      }
      
      if (testResults.firebase && testResults.firebase.status !== 'success') {
        setError('Authentication service is not properly configured. Please contact support.');
        setLoading(false);
        return;
      }
      
      // Use the Firebase function
      await sendPasswordResetEmail(email);
      setMessage(
        'Password reset email sent! Check your inbox (and spam folder). ' +
        'After resetting your password, you will be automatically redirected to the login page.'
      );
    } catch (err) {
      console.error("Password Reset Error:", err);
      let errorMessage = 'Failed to send password reset email. Please try again.';
      
      // Provide more specific error messages
      if (err.message.includes('auth/user-not-found')) {
        errorMessage = 'No account found with this email address.';
      } else if (err.message.includes('auth/invalid-email')) {
        errorMessage = 'Invalid email format. Please check your email address.';
      } else if (err.message.includes('network')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 px-4">
      <div className="w-full max-w-md bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">Reset Password</h2>
        
        {error && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">{error}</div>}
        {message && <div className="mb-4 p-3 bg-green-100 text-green-700 rounded">{message}</div>}
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="email" className="block text-gray-700 text-sm font-bold mb-2">Email Address</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          
          <button
            type="submit"
            disabled={loading}
            className={`w-full bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {loading ? 'Sending...' : 'Send Reset Email'}
          </button>
        </form>
        
        <div className="mt-4 text-center text-sm">
          <Link to="/login" className="text-blue-500 hover:text-blue-700">
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword;